import { useEffect } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";
import { useCustomerSummary } from "@api/intelligenceController";
import { markdownComponents } from "@areas/chatbot/utils";
import AiSparkleIcon from "./AiSparkleIcon";

// Break the read into sections that each begin with an emoji, so they can fade in one after
// another. Lines before the first emoji (and any non-emoji lines) attach to the current section.
function splitIntoSections(markdown) {
  const startsWithEmoji = (line) => /^\s*\p{Extended_Pictographic}/u.test(line);
  const sections = [];
  for (const line of markdown.split("\n")) {
    if (startsWithEmoji(line) || sections.length === 0) {
      sections.push(line);
    } else {
      sections[sections.length - 1] += `\n${line}`;
    }
  }
  return sections.filter((section) => section.trim() !== "");
}

export default function SummaryCard({ customerId, onReadyChange }) {
  const { data, isFetching, isError, refresh } =
    useCustomerSummary(customerId);
  const summary = data?.summary ?? "";

  // The read is "ready" once it has loaded (or errored / has nothing to show) — the composer
  // holds its example placeholder back until then. A refresh (isFetching) puts the loading
  // placeholder back up, so it is not ready again until that resolves.
  const readReady = isError || !isFetching;
  useEffect(() => {
    onReadyChange?.(readReady);
  }, [readReady, onReadyChange]);

  if (isError) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
          <AiSparkleIcon className="size-4" />
          Ontik read
        </span>
        <button
          type="button"
          onClick={refresh}
          disabled={isFetching}
          aria-label="Refresh Ontik read"
          className="text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
        >
          <ArrowPathIcon
            aria-hidden="true"
            className={`size-4 ${isFetching ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {isFetching ? (
        // Roughly the height of a real read, so content landing doesn't jolt the panel. Varied
        // widths + a staggered pulse read as flowing paragraphs rather than a flat block.
        <div className="mt-3 space-y-2">
          {[
            "w-full", "w-11/12", "w-full", "w-10/12", "w-9/12",
            "w-full", "w-11/12", "w-full", "w-10/12", "w-9/12",
            "w-full", "w-11/12", "w-full", "w-8/12", "w-2/3",
          ].map((width, i) => (
            <div
              key={i}
              className={`h-3 ${width} animate-pulse rounded bg-zinc-200`}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      ) : (
        // Keyed on the text so a new read (refresh, or switching customer) re-runs the fade.
        // Each emoji-led section fades in after the previous one for a sectioned reveal.
        <div key={summary} className="mt-3 space-y-2 text-sm leading-relaxed text-zinc-700">
          {splitIntoSections(summary).map((section, i) => (
            <div
              key={i}
              className="animate-readIn"
              style={{ animationDelay: `${i * 180}ms` }}
            >
              <ReactMarkdown components={markdownComponents}>
                {section}
              </ReactMarkdown>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
